import { shorthands } from '@tamagui/shorthands'
import { createFont, createTamagui, isWeb } from 'tamagui'

import { animations } from './animations'
import { themes } from './theme'
import { tokens } from './tokens'

// Web font CSS variables
const webFontRegular = 'var(--font-obviously-regular)'
const webFontNarrow = 'var(--font-obviously-narrow)'
const webFontCondensed = 'var(--font-obviously-condensed)'
const webFontExtended = 'var(--font-obviously-extended)'
const webFontWide = 'var(--font-obviously-wide)'

// Font weights per category
const defaultWeights = {
  400: '400',
  500: '500',
  600: '600',
  700: '700',
  800: '800',
  true: '400',
}

const bodyWeights = defaultWeights
const displayWeights = { 700: '700', true: '700' }
const headingWeights = { 600: '600', 700: '700', true: '600' }
const titleWeights = { 500: '500', 600: '600', true: '500' }
const extendedWeights = { 700: '700', true: '700' }
const letterSpacing = { true: 0 }

type SizeVariant = 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
type FontSizes = Record<SizeVariant | 'true', number> & typeof defaultSizes
type FontLineHeights = Record<SizeVariant | 'true', number> &
  typeof defaultLineHeights
type SemanticFontConfig = {
  weight: Record<number | 'true', string>
  letterSpacing: typeof letterSpacing
  size: FontSizes
  lineHeight: FontLineHeights
}

// Default sizes
const defaultSizes = {
  0.75: 8,
  1: 10,
  2: 12,
  3: 14,
  3.5: 15,
  4: 16,
  5: 18,
  6: 20,
  '6.5': 22,
  7: 24,
  '7.5': 28,
  8: 30,
  9: 36,
  '9.5': 40,
  10: 48,
  '10.5': 56,
  11: 60,
  '11.5': 68,
  12: 72,
  13: 96,
  '13.5': 124,
  14: 128,
  15: 144,
  16: 156,
  17: 192,
}

const defaultLineHeights = {
  0.75: 11,
  '1': 13,
  '2': 15,
  '3': 18,
  '3.5': 19,
  '4': 20,
  '5': 22,
  '6': 25,
  '6.5': 27,
  '7': 29,
  '7.5': 32,
  '8': 37,
  '9': 44,
  '9.5': 48,
  '10': 59,
  '10.5': 63,
  '11': 73,
  '11.5': 83,
  '12': 87,
  '13': 116,
  '13.5': 125,
  '14': 154,
  '15': 169,
  '16': 185,
  '17': 225,
}

const defaultFontConfig = {
  weight: defaultWeights,
  letterSpacing,
  size: { ...defaultSizes, true: 16 },
  lineHeight: { ...defaultLineHeights, true: 20 },
}

// Semantic font configs

const bodyFontConfig = {
  weight: bodyWeights,
  letterSpacing,
  size: { ...defaultSizes, xs: 7, s: 10, m: 12, l: 16, xl: 20, xxl: 26, true: 16 },
  lineHeight: {
    ...defaultLineHeights,
    xs: 10,
    s: 14,
    m: 17,
    l: 19,
    xl: 24,
    xxl: 38,
    true: 19,
  },
} satisfies SemanticFontConfig

const displayFontConfig = {
  weight: displayWeights,
  letterSpacing,
  size: { ...defaultSizes, xs: 14, s: 18, m: 22, l: 28, xl: 40, xxl: 53, true: 22 },
  lineHeight: {
    ...defaultSizes,
    xs: 14,
    s: 18,
    m: 22,
    l: 28,
    xl: 40,
    xxl: 53,
    true: 22,
  },
} satisfies SemanticFontConfig

const extendedFontConfig = {
  ...displayFontConfig,
  weight: extendedWeights,
} satisfies SemanticFontConfig

const headingFontConfig = {
  weight: headingWeights,
  letterSpacing,
  size: { ...defaultSizes, xs: 14, s: 18, m: 22, l: 28, xl: 36, xxl: 48, true: 22 },
  lineHeight: {
    ...defaultLineHeights,
    xs: 17,
    s: 22,
    m: 22,
    l: 28,
    xl: 36,
    xxl: 48,
    true: 22,
  },
} satisfies SemanticFontConfig

const titleFontConfig = {
  weight: titleWeights,
  letterSpacing,
  size: { ...defaultSizes, xs: 10, s: 12, m: 14, l: 16, xl: 18, xxl: 24, true: 14 },
  lineHeight: {
    ...defaultLineHeights,
    xs: 12,
    s: 14,
    m: 17,
    l: 19,
    xl: 22,
    xxl: 32,
    true: 17,
  },
} satisfies SemanticFontConfig



// Font face helper
const face = (webFont: string, nativeFont: string) => ({
  normal: isWeb ? webFont : nativeFont,
})

const families = {
  regular: isWeb ? webFontRegular : 'Obviously-Regular',
  narrow: isWeb ? webFontNarrow : 'Obviously-NarrowBold',
  condensed: isWeb ? webFontCondensed : 'Obviously-CondensedBold',
  extended: isWeb ? webFontExtended : 'Obviously-ExtendedBold',
  wide: isWeb ? webFontWide : 'Obviously-WideBold',

}

const faces = {
  regular: {
    '400': face(webFontRegular, 'Obviously-Regular'),
    '500': face(webFontRegular, 'Obviously-Medium'),
    '600': face(webFontRegular, 'Obviously-Semibold'),
    '700': face(webFontRegular, 'Obviously-Bold'),
    '800': face(webFontRegular, 'Obviously-Black'),
  },
  narrow: {
    '700': face(webFontNarrow, 'Obviously-NarrowBold'),
    '800': face(webFontNarrow, 'Obviously-NarrowBlack'),
  },
  condensed: {
    '700': face(webFontCondensed, 'Obviously-CondensedBold'),
    '800': face(webFontCondensed, 'Obviously-CondensedBlack'),
  },
  wide: {
    '700': face(webFontWide, 'Obviously-WideBold'),
  },
  extended: {
    '700': face(webFontExtended, 'Obviously-ExtendedBold'),
  },
}

// Semantic fonts
const body = createFont({
  family: families.regular,
  face: faces.regular,
  ...bodyFontConfig,
})

const display = createFont({
  family: families.wide,
  face: faces.wide,
  ...displayFontConfig,
})

const heading = createFont({
  family: families.regular,
  face: faces.regular,
  ...headingFontConfig,
})

const title = createFont({
  family: families.regular,
  face: faces.regular,
  ...titleFontConfig,
})

const narrow = createFont({
  family: families.narrow,
  face: faces.narrow,
  ...defaultFontConfig,
})

const condensed = createFont({
  family: families.condensed,
  face: faces.condensed,
  ...defaultFontConfig,
})

const extended = createFont({
  family: families.extended,
  face: faces.extended,
  ...extendedFontConfig,
})

export const config = createTamagui({
  animations,
  defaultTheme: 'light',
  shouldAddPrefersColorThemes: false,
  themeClassNameOnRoot: true,
  settings: { onlyAllowShorthands: false },
  shorthands: { ...shorthands },
  fonts: { body, display, heading, title, narrow, condensed, extended },
  themes,
  tokens,
  media: {
    xxxs: { maxWidth: 375 },
    xxs: { maxWidth: 475 },
    xs: { maxWidth: 660 },
    sm: { maxWidth: 800 },
    md: { maxWidth: 1020 },
    lg: { maxWidth: 1280 },
    xl: { maxWidth: 1420 },
    xxl: { maxWidth: 1780 },
    gtXxxs: { minWidth: 375 + 1 },
    gtXxs: { minWidth: 475 + 1 },
    gtXs: { minWidth: 660 + 1 },
    gtSm: { minWidth: 800 + 1 },
    gtMd: { minWidth: 1020 + 1 },
    gtLg: { minWidth: 1280 + 1 },
    gtXxl: { minWidth: 1780 + 1 },
    short: { maxHeight: 820 },
    tall: { minHeight: 820 },
    hoverNone: { hover: 'none' },
    pointerCoarse: { pointer: 'coarse' },
  },
})
